/**
 * Tela de Cursos e turmas: as turmas em cartoes (curso, datas de cada
 * modulo, vagas e matriculados), as matriculas com a ficha do aluno e o
 * catalogo de cursos e precos.
 *
 * A matricula escolhe a pessoa pela busca de Pessoas e ja vem com o preco do
 * curso na modalidade, que a Jaque pode mudar (desconto, condicao especial).
 * A vaga e conferida no servidor; a tela so mostra o que sobra.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import { useSessao } from '../../sessao.tsx';
import { ErroApi, escrever, ler, mensagemDe } from '../../ui/api.ts';
import {
  Aviso, Barra, Botao, Busca, Campo, Formulario, Painel, Selo, Tabela, type Coluna, type Tom,
} from '../../ui/componentes.tsx';
import { centavosDe, data, reais } from '../../ui/formatos.ts';
import type { TelaDeModulo } from '../contrato.ts';
import './turmas.css';

// Tipos que a API devolve (modules/turmas/src/tools.ts)

type Modalidade = 'a_vista' | 'parcelado';
type StatusTurma = 'planejada' | 'aberta' | 'lotada' | 'encerrada' | 'cancelada';
type StatusMatricula = 'reservada' | 'confirmada' | 'cancelada';
type StatusFicha = 'sem_ficha' | 'a_conferir' | 'confirmada';

type Preco = {
  modalidade: Modalidade;
  valor_centavos: number;
  valor_formatado: string;
  parcelas_max: number | null;
};

type Curso = {
  id: string;
  codigo: string;
  nome: string;
  descricao: string | null;
  qtd_modulos: number;
  dias_por_modulo: number;
  ativo: boolean;
  precos: Preco[];
};

type Modulo = { numero: number; data_inicio: string; data_fim: string };

type Turma = {
  id: string;
  nome: string;
  cidade: string | null;
  local: string | null;
  vagas: number;
  ocupadas: number;
  vagas_restantes: number;
  lotada: boolean;
  status: StatusTurma;
  observacoes: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  modulos: Modulo[];
  curso: Curso;
};

type Matricula = {
  id: string;
  turma_id: string;
  pessoa_id: string;
  lead_id: string | null;
  modulo_numero: number | null;
  modalidade: Modalidade;
  valor_centavos: number;
  valor_formatado: string;
  parcelas: number;
  status: StatusMatricula;
  observacoes: string | null;
  criado_em: string;
  pessoa: { id: string; nome: string; telefone: string | null;
    telefone_formatado: string | null; email: string | null; ficha_status: StatusFicha };
  turma_nome: string;
  curso_nome: string;
  curso_codigo: string;
};

type PessoaBusca = {
  id: string;
  nome: string;
  telefone_formatado: string | null;
  email: string | null;
  ficha_status: StatusFicha;
};

// Rotulos

const STATUS_TURMA: Record<StatusTurma, { rotulo: string; tom: Tom }> = {
  planejada: { rotulo: 'Planejada', tom: 'neutro' },
  aberta: { rotulo: 'Aberta', tom: 'ok' },
  lotada: { rotulo: 'Lotada', tom: 'aviso' },
  encerrada: { rotulo: 'Encerrada', tom: 'neutro' },
  cancelada: { rotulo: 'Cancelada', tom: 'erro' },
};

const STATUS_MATRICULA: Record<StatusMatricula, { rotulo: string; tom: Tom }> = {
  reservada: { rotulo: 'Reservada', tom: 'info' },
  confirmada: { rotulo: 'Confirmada', tom: 'ok' },
  cancelada: { rotulo: 'Cancelada', tom: 'neutro' },
};

const FICHA: Record<StatusFicha, { rotulo: string; tom: Tom }> = {
  sem_ficha: { rotulo: 'Sem ficha', tom: 'neutro' },
  a_conferir: { rotulo: 'Ficha a conferir', tom: 'aviso' },
  confirmada: { rotulo: 'Ficha confirmada', tom: 'ok' },
};

const MODALIDADE: Record<Modalidade, string> = { a_vista: 'À vista', parcelado: 'Parcelado' };

// Apoio

function usePermissao(codigo: string): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes(codigo);
}

/** "05/03 a 07/03/2027"; o mesmo dia vira uma data so. */
function periodo(inicio: string, fim: string): string {
  if (inicio === fim) return data(inicio);
  return inicio.slice(0, 4) === fim.slice(0, 4)
    ? `${data(inicio).slice(0, 5)} a ${data(fim)}`
    : `${data(inicio)} a ${data(fim)}`;
}

/** Soma dias a uma data AAAA-MM-DD. */
function somarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

const precoDe = (curso: Curso | undefined, m: Modalidade) =>
  curso?.precos.find((p) => p.modalidade === m);

const textoParcelas = (m: Pick<Matricula, 'modalidade' | 'parcelas'>) =>
  m.modalidade === 'a_vista' ? 'À vista' : `${m.parcelas}x`;

/** Todas as mensagens do erro, para o formulario mostrar de uma vez. */
function mensagensDe(e: unknown): string {
  if (e instanceof ErroApi && e.falhas.length > 1) {
    return e.falhas.map((f) => f.mensagem).filter(Boolean).join(' ');
  }
  return mensagemDe(e);
}

/** Selo da ficha com o link que abre a ficha da pessoa em Pessoas. */
function SeloFicha({ status, pessoaId }: { status: StatusFicha; pessoaId: string }) {
  const f = FICHA[status];
  const href = `/pessoas?id=${pessoaId}`;
  return (
    <span className="matricula-ficha">
      <Selo tom={f.tom}>{f.rotulo}</Selo>
      {status === 'a_conferir' && <Link to={href} onClick={(e) => e.stopPropagation()}>Conferir</Link>}
      {status === 'sem_ficha' && <Link to={href} onClick={(e) => e.stopPropagation()}>Preencher</Link>}
    </span>
  );
}

/**
 * ?id=<uuid> abre o registro direto (link de tarefa, contrato, lead). Devolve
 * o id pedido e a funcao que o tira da URL ao fechar o painel.
 */
function useIdPedido(): [string | null, () => void] {
  const [busca, setBusca] = useSearchParams();
  const id = busca.get('id');
  const limpar = () => {
    if (id === null) return;
    const resto = new URLSearchParams(busca);
    resto.delete('id');
    setBusca(resto, { replace: true });
  };
  return [id, limpar];
}

/** Abre o item da lista cujo id veio na URL, uma vez, quando a lista chega. */
function useAbrirPedido<T extends { id: string }>(
  itens: readonly T[] | undefined, abrir: (item: T) => void,
): () => void {
  const [id, limpar] = useIdPedido();
  useEffect(() => {
    if (id === null || itens === undefined) return;
    const achado = itens.find((i) => i.id === id);
    if (achado !== undefined) abrir(achado);
  }, [id, itens]);
  return limpar;
}

const useCursos = (incluirInativos = false) => useQuery({
  queryKey: ['turmas', 'cursos', incluirInativos],
  queryFn: () => ler<{ itens: Curso[] }>('turmas.cursos_listar',
    incluirInativos ? { incluir_inativos: true } : {}),
});

const useTurmas = () => useQuery({
  queryKey: ['turmas', 'lista'],
  queryFn: () => ler<{ itens: Turma[] }>('turmas.listar', { limite: 200 }),
});

// Aba Turmas

type FormTurma = {
  curso_id: string;
  nome: string;
  cidade: string;
  local: string;
  vagas: string;
  status: StatusTurma;
  observacoes: string;
  modulos: Record<number, { inicio: string; fim: string }>;
};

function formDaTurma(t: Turma | null, cursoId: string): FormTurma {
  return {
    curso_id: t?.curso.id ?? cursoId,
    nome: t?.nome ?? '',
    cidade: t?.cidade ?? '',
    local: t?.local ?? '',
    vagas: t !== null ? String(t.vagas) : '20',
    status: t?.status ?? 'planejada',
    observacoes: t?.observacoes ?? '',
    modulos: Object.fromEntries((t?.modulos ?? []).map((m) =>
      [m.numero, { inicio: m.data_inicio, fim: m.data_fim }])),
  };
}

function PainelTurma({ turma, cursos, aoFechar }: {
  turma: Turma | null; cursos: Curso[]; aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const ativos = cursos.filter((c) => c.ativo || c.id === turma?.curso.id);
  const [form, setForm] = useState<FormTurma>(() => formDaTurma(turma, ativos[0]?.id ?? ''));
  const [erro, setErro] = useState<unknown>(null);
  const podeEditar = usePermissao('turmas.editar');
  const podeExcluir = usePermissao('turmas.excluir');
  const curso = cursos.find((c) => c.id === form.curso_id);
  const numeros = Array.from({ length: curso?.qtd_modulos ?? 0 }, (_, i) => i + 1);

  const atualizar = () => qc.invalidateQueries({ queryKey: ['turmas'] });

  const salvar = useMutation({
    mutationFn: () => escrever<Turma>('turmas.salvar', {
      ...(turma !== null ? { id: turma.id } : {}),
      curso_id: form.curso_id,
      nome: form.nome,
      cidade: form.cidade.trim() === '' ? null : form.cidade,
      local: form.local.trim() === '' ? null : form.local,
      vagas: Number(form.vagas),
      status: form.status,
      observacoes: form.observacoes.trim() === '' ? null : form.observacoes,
      // so os modulos do curso que tem as duas datas
      modulos: numeros
        .map((n) => ({ numero: n, data_inicio: form.modulos[n]?.inicio ?? '',
          data_fim: form.modulos[n]?.fim ?? '' }))
        .filter((m) => m.data_inicio !== '' && m.data_fim !== ''),
    }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
    onError: setErro,
  });

  const excluir = useMutation({
    mutationFn: () => escrever('turmas.excluir', { id: turma!.id }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
    onError: setErro,
  });

  const campo = <K extends keyof FormTurma>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  /** Inicio preenchido e fim vazio: o fim sai dos dias por modulo do curso. */
  const mudarData = (n: number, qual: 'inicio' | 'fim', valor: string) => setForm((f) => {
    const atual = f.modulos[n] ?? { inicio: '', fim: '' };
    const novo = { ...atual, [qual]: valor };
    if (qual === 'inicio' && valor !== '' && atual.fim === '' && curso !== undefined) {
      novo.fim = somarDias(valor, curso.dias_por_modulo - 1);
    }
    return { ...f, modulos: { ...f.modulos, [n]: novo } };
  });

  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar}
      titulo={turma === null ? 'Nova turma' : turma.nome}
      subtitulo={turma === null ? 'Curso, vagas e as datas de cada módulo'
        : `${turma.ocupadas} de ${turma.vagas} vagas ocupadas`}
      rodape={(
        <>
          {turma !== null && podeExcluir && (
            <Botao variante="perigo" desativado={excluir.isPending}
              aoClicar={() => { if (window.confirm(`Excluir a turma ${turma.nome}?`)) excluir.mutate(); }}>
              Excluir
            </Botao>
          )}
          {podeEditar && (
            <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
              {salvar.isPending ? 'Salvando' : 'Salvar'}
            </Botao>
          )}
        </>
      )}
    >
      {erro !== null && <Aviso tom="erro">{mensagensDe(erro)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Curso" erro={erroDo('curso_id')}>
          {(id) => (
            <select id={id} value={form.curso_id} onChange={campo('curso_id')}
              disabled={!podeEditar || (turma !== null && turma.ocupadas > 0)}>
              {ativos.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          )}
        </Campo>
        <Campo rotulo="Nome da turma" erro={erroDo('nome')}>
          {(id) => <input id={id} value={form.nome} onChange={campo('nome')} disabled={!podeEditar}
            placeholder="Formação São Paulo 2027" />}
        </Campo>
        <Campo rotulo="Cidade" largura="metade" erro={erroDo('cidade')}>
          {(id) => <input id={id} value={form.cidade} onChange={campo('cidade')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Local" largura="metade" erro={erroDo('local')}>
          {(id) => <input id={id} value={form.local} onChange={campo('local')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Vagas" largura="metade" erro={erroDo('vagas')}>
          {(id) => <input id={id} type="number" min={1} value={form.vagas} onChange={campo('vagas')}
            disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Status" largura="metade" erro={erroDo('status')}
          dica="Aberta exige as datas de todos os módulos.">
          {(id) => (
            <select id={id} value={form.status} onChange={campo('status')} disabled={!podeEditar}>
              {(Object.keys(STATUS_TURMA) as StatusTurma[]).map((s) => (
                <option key={s} value={s}>{STATUS_TURMA[s].rotulo}</option>
              ))}
            </select>
          )}
        </Campo>
        <div className="turma-form-modulos">
          <span className="ui-campo__rotulo">
            Datas dos módulos{curso !== undefined ? ` (${curso.dias_por_modulo} dia(s) cada)` : ''}
          </span>
          {numeros.map((n) => (
            <div key={n} className="turma-form-modulo">
              <span className="turma-form-modulo__nome">Módulo {n}</span>
              <input type="date" aria-label={`Início do módulo ${n}`} disabled={!podeEditar}
                value={form.modulos[n]?.inicio ?? ''}
                onChange={(e) => mudarData(n, 'inicio', e.target.value)} />
              <input type="date" aria-label={`Fim do módulo ${n}`} disabled={!podeEditar}
                value={form.modulos[n]?.fim ?? ''}
                onChange={(e) => mudarData(n, 'fim', e.target.value)} />
            </div>
          ))}
        </div>
        <Campo rotulo="Observações" erro={erroDo('observacoes')}>
          {(id) => <textarea id={id} value={form.observacoes} onChange={campo('observacoes')}
            disabled={!podeEditar} />}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

function CartaoTurma({ turma, alunos, aoEditar, aoMatricular }: {
  turma: Turma;
  alunos: Matricula[];
  aoEditar?: (() => void) | undefined;
  aoMatricular?: (() => void) | undefined;
}) {
  const s = STATUS_TURMA[turma.status];
  const pct = Math.min(100, Math.round((turma.ocupadas / turma.vagas) * 100));
  const lugar = [turma.cidade, turma.local].filter(Boolean).join(', ');
  return (
    <article className="turma-cartao">
      <header className="turma-cartao__cabeca">
        <div>
          <h3 className="turma-cartao__titulo">{turma.nome}</h3>
          <p className="turma-cartao__curso">{turma.curso.nome}{lugar !== '' ? ` · ${lugar}` : ''}</p>
        </div>
        <div className="turma-cartao__selos">
          {turma.lotada && turma.status !== 'lotada' && <Selo tom="aviso">Lotada</Selo>}
          <Selo tom={s.tom}>{s.rotulo}</Selo>
        </div>
      </header>

      <div className="turma-cartao__secao">
        <span className="turma-cartao__rotulo">Datas</span>
        {turma.modulos.length === 0
          ? <span className="turma-alunos__vazio">Datas a definir</span>
          : (
            <ul className="turma-modulos">
              {turma.modulos.map((m) => (
                <li key={m.numero}>
                  <span>{turma.curso.qtd_modulos > 1 ? `Módulo ${m.numero}` : 'Curso'}</span>
                  <span>{periodo(m.data_inicio, m.data_fim)}</span>
                </li>
              ))}
            </ul>
          )}
      </div>

      <div className="turma-vagas">
        <div className="turma-vagas__texto">
          <span>Vagas</span>
          <strong>{turma.ocupadas}/{turma.vagas}</strong>
        </div>
        <div className="turma-vagas__trilho" role="progressbar" aria-valuemin={0}
          aria-valuemax={turma.vagas} aria-valuenow={turma.ocupadas}
          aria-label={`${turma.ocupadas} de ${turma.vagas} vagas ocupadas`}>
          <div className={`turma-vagas__ocupado${turma.lotada ? ' turma-vagas__ocupado--cheia' : ''}`}
            style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="turma-cartao__secao">
        <span className="turma-cartao__rotulo">Matriculados</span>
        {alunos.length === 0
          ? <span className="turma-alunos__vazio">Ninguém matriculado ainda.</span>
          : (
            <ul className="turma-alunos">
              {alunos.map((m) => (
                <li key={m.id}>
                  <span className="turma-alunos__nome">{m.pessoa.nome}</span>
                  <Selo tom={STATUS_MATRICULA[m.status].tom}>{STATUS_MATRICULA[m.status].rotulo}</Selo>
                </li>
              ))}
            </ul>
          )}
      </div>

      {(aoEditar !== undefined || aoMatricular !== undefined) && (
        <div className="turma-cartao__acoes">
          {aoMatricular !== undefined && !turma.lotada
            && (turma.status === 'aberta' || turma.status === 'planejada') && (
            <Botao variante="primario" aoClicar={aoMatricular}>Matricular</Botao>
          )}
          {aoEditar !== undefined && <Botao aoClicar={aoEditar}>Editar</Botao>}
        </div>
      )}
    </article>
  );
}

function AbaTurmas() {
  const cursos = useCursos(true);
  const turmas = useTurmas();
  const matriculas = useQuery({
    queryKey: ['turmas', 'matriculas', 'ativas'],
    queryFn: () => ler<{ itens: Matricula[] }>('turmas.matriculas_listar', { limite: 500 }),
  });
  const [cursoFiltro, setCursoFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<'' | StatusTurma>('');
  const [aberta, setAberta] = useState<Turma | 'nova' | null>(null);
  const [matricular, setMatricular] = useState<Turma | null>(null);
  const podeEditar = usePermissao('turmas.editar');
  const podeMatricular = usePermissao('turmas.matricular');
  const limparPedido = useAbrirPedido(turmas.data?.itens, setAberta);

  const porTurma = useMemo(() => {
    const mapa = new Map<string, Matricula[]>();
    for (const m of matriculas.data?.itens ?? []) {
      mapa.set(m.turma_id, [...(mapa.get(m.turma_id) ?? []), m]);
    }
    return mapa;
  }, [matriculas.data]);

  const itens = (turmas.data?.itens ?? []).filter((t) =>
    (cursoFiltro === '' || t.curso.id === cursoFiltro)
    && (statusFiltro === '' || t.status === statusFiltro));

  return (
    <>
      <Barra acoes={podeEditar && (cursos.data?.itens.length ?? 0) > 0
        ? <Botao variante="primario" aoClicar={() => setAberta('nova')}>Nova turma</Botao>
        : undefined}>
        <select aria-label="Curso" value={cursoFiltro} onChange={(e) => setCursoFiltro(e.target.value)}>
          <option value="">Todos os cursos</option>
          {(cursos.data?.itens ?? []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select aria-label="Status da turma" value={statusFiltro}
          onChange={(e) => setStatusFiltro(e.target.value as '' | StatusTurma)}>
          <option value="">Todos os status</option>
          {(Object.keys(STATUS_TURMA) as StatusTurma[]).map((s) => (
            <option key={s} value={s}>{STATUS_TURMA[s].rotulo}</option>
          ))}
        </select>
      </Barra>

      {turmas.isPending && <EstadoCarregando texto="Carregando turmas" />}
      {turmas.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(turmas.error)} />}
      {turmas.isSuccess && itens.length === 0 && (
        <EstadoVazio
          titulo={turmas.data.itens.length === 0 ? 'Nenhuma turma ainda' : 'Nenhuma turma neste filtro'}
          texto={turmas.data.itens.length === 0
            ? 'Crie a primeira turma com o curso, as vagas e as datas de cada módulo.'
            : 'Mude o curso ou o status para ver outras turmas.'} />
      )}
      {turmas.isSuccess && itens.length > 0 && (
        <div className="turmas-grade">
          {itens.map((t) => (
            <CartaoTurma key={t.id} turma={t} alunos={porTurma.get(t.id) ?? []}
              aoEditar={podeEditar ? () => setAberta(t) : undefined}
              aoMatricular={podeMatricular ? () => setMatricular(t) : undefined} />
          ))}
        </div>
      )}

      {aberta !== null && cursos.data !== undefined && (
        <PainelTurma key={aberta === 'nova' ? 'nova' : aberta.id}
          turma={aberta === 'nova' ? null : aberta} cursos={cursos.data.itens}
          aoFechar={() => { setAberta(null); limparPedido(); }} />
      )}
      {matricular !== null && (
        <PainelNovaMatricula turmaInicial={matricular.id} aoFechar={() => setMatricular(null)} />
      )}
    </>
  );
}

// Aba Matriculas

function EscolherPessoa({ escolhida, aoEscolher }: {
  escolhida: PessoaBusca | null; aoEscolher: (p: PessoaBusca | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const q = useQuery({
    queryKey: ['pessoas', 'busca-matricula', termo],
    queryFn: () => ler<{ itens: PessoaBusca[] }>('pessoas.buscar', { termo, limite: 8 }),
    enabled: termo.trim().length >= 2,
  });
  if (escolhida !== null) {
    return (
      <div className="matricula-escolhida">
        <div className="ui-item__principal">
          <span className="ui-item__nome">{escolhida.nome}</span>
          <span className="ui-item__detalhe">
            {[escolhida.telefone_formatado, escolhida.email].filter(Boolean).join(' · ')}
          </span>
        </div>
        <SeloFicha status={escolhida.ficha_status} pessoaId={escolhida.id} />
        <Botao variante="fantasma" aoClicar={() => aoEscolher(null)}>Trocar</Botao>
      </div>
    );
  }
  return (
    <div>
      <Busca valor={termo} aoMudar={setTermo} dica="Buscar aluno por nome, telefone ou CPF" />
      <div className="matricula-pessoas">
        {q.isFetching && <span className="ui-item__detalhe">Buscando</span>}
        {q.isError && <span className="ui-campo__erro">{mensagemDe(q.error)}</span>}
        {q.isSuccess && q.data.itens.length === 0 && (
          <span className="ui-item__detalhe">
            Ninguém encontrado. Cadastre a pessoa em <Link to="/pessoas">Pessoas</Link>.
          </span>
        )}
        {q.data?.itens.map((p) => (
          <button key={p.id} type="button" className="matricula-pessoa" onClick={() => aoEscolher(p)}>
            <span className="ui-item__principal">
              <span className="ui-item__nome">{p.nome}</span>
              <span className="ui-item__detalhe">{p.telefone_formatado ?? p.email ?? ''}</span>
            </span>
            <Selo tom={FICHA[p.ficha_status].tom}>{FICHA[p.ficha_status].rotulo}</Selo>
          </button>
        ))}
      </div>
    </div>
  );
}

function PainelNovaMatricula({ turmaInicial, aoFechar }: {
  turmaInicial: string; aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const turmas = useTurmas();
  const abertas = (turmas.data?.itens ?? []).filter((t) =>
    (t.status === 'aberta' || t.status === 'planejada') && !t.lotada);
  const [turmaId, setTurmaId] = useState(turmaInicial);
  const [pessoa, setPessoa] = useState<PessoaBusca | null>(null);
  const [modalidade, setModalidade] = useState<Modalidade>('a_vista');
  const [valor, setValor] = useState('');
  const [parcelas, setParcelas] = useState('');
  const [modulo, setModulo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<unknown>(null);

  const turma = abertas.find((t) => t.id === turmaId);
  const preco = precoDe(turma?.curso, modalidade);

  // trocar turma ou modalidade traz o preco do curso de novo (so isso: um
  // refetch da lista nao apaga o valor que a Jaque digitou)
  const precoCentavos = preco?.valor_centavos;
  const precoParcelas = preco?.parcelas_max ?? null;
  useEffect(() => {
    if (precoCentavos === undefined) return;
    setValor(reais(precoCentavos));
    setParcelas(modalidade === 'parcelado' && precoParcelas !== null ? String(precoParcelas) : '');
  }, [turmaId, modalidade, precoCentavos, precoParcelas]);

  const criar = useMutation({
    mutationFn: () => {
      const centavos = centavosDe(valor);
      return escrever('turmas.matricula_criar', {
        turma_id: turmaId,
        pessoa_id: pessoa?.id,
        modalidade,
        ...(centavos !== null ? { valor_centavos: centavos } : {}),
        ...(modalidade === 'parcelado' && parcelas !== '' ? { parcelas: Number(parcelas) } : {}),
        ...(modulo !== '' ? { modulo_numero: Number(modulo) } : {}),
        ...(observacoes.trim() !== '' ? { observacoes } : {}),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['turmas'] });
      aoFechar();
    },
    onError: setErro,
  });

  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar} titulo="Nova matrícula"
      subtitulo="A matrícula nasce reservada e é confirmada quando o contrato é assinado."
      rodape={(
        <Botao variante="primario" desativado={criar.isPending || pessoa === null || turma === undefined}
          aoClicar={() => criar.mutate()}>
          {criar.isPending ? 'Matriculando' : 'Matricular'}
        </Botao>
      )}
    >
      {erro !== null && <Aviso tom="erro">{mensagensDe(erro)}</Aviso>}
      {turmas.isSuccess && abertas.length === 0 && (
        <Aviso tom="aviso">Nenhuma turma aberta com vaga. Abra uma turma ou aumente as vagas.</Aviso>
      )}
      <Formulario aoEnviar={() => criar.mutate()}>
        <Campo rotulo="Aluno" erro={erroDo('pessoa_id')}>
          {() => <EscolherPessoa escolhida={pessoa} aoEscolher={setPessoa} />}
        </Campo>
        <Campo rotulo="Turma" erro={erroDo('turma_id')}
          {...(turma !== undefined ? { dica: `${turma.vagas_restantes} vaga(s) restante(s)` } : {})}>
          {(id) => (
            <select id={id} value={turmaId} onChange={(e) => setTurmaId(e.target.value)}>
              {turma === undefined && <option value="">Escolha a turma</option>}
              {abertas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} ({t.curso.nome}{t.data_inicio !== null ? `, ${data(t.data_inicio)}` : ''})
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Modalidade" largura="metade" erro={erroDo('modalidade')}>
          {(id) => (
            <select id={id} value={modalidade} onChange={(e) => setModalidade(e.target.value as Modalidade)}>
              <option value="a_vista">À vista</option>
              <option value="parcelado">Parcelado</option>
            </select>
          )}
        </Campo>
        {modalidade === 'parcelado' && (
          <Campo rotulo="Parcelas" largura="metade" erro={erroDo('parcelas')}
            dica={preco?.parcelas_max != null ? `Até ${preco.parcelas_max}x` : 'A combinar'}>
            {(id) => <input id={id} type="number" min={1} max={36} value={parcelas}
              onChange={(e) => setParcelas(e.target.value)} />}
          </Campo>
        )}
        <Campo rotulo="Valor" largura="metade" erro={erroDo('valor_centavos')}
          {...(preco !== undefined ? { dica: `Preço do curso: ${preco.valor_formatado}` } : {})}>
          {(id) => <input id={id} inputMode="decimal" value={valor}
            onChange={(e) => setValor(e.target.value)} />}
        </Campo>
        {turma?.curso.codigo === 'revisao' && (
          <Campo rotulo="Módulo revisado" largura="metade" erro={erroDo('modulo_numero')}>
            {(id) => <input id={id} type="number" min={1} max={12} value={modulo}
              onChange={(e) => setModulo(e.target.value)} />}
          </Campo>
        )}
        <Campo rotulo="Observações" erro={erroDo('observacoes')}>
          {(id) => <textarea id={id} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

function PainelMatricula({ matricula, aoFechar }: { matricula: Matricula; aoFechar: () => void }) {
  const qc = useQueryClient();
  const podeAlterar = usePermissao('turmas.matricular');
  const [status, setStatus] = useState<StatusMatricula>(matricula.status);
  const [modalidade, setModalidade] = useState<Modalidade>(matricula.modalidade);
  const [valor, setValor] = useState(reais(matricula.valor_centavos));
  const [parcelas, setParcelas] = useState(String(matricula.parcelas));
  const [observacoes, setObservacoes] = useState(matricula.observacoes ?? '');
  const [erro, setErro] = useState<unknown>(null);

  const salvar = useMutation({
    mutationFn: () => {
      const centavos = centavosDe(valor);
      // trocar a modalidade sem mexer no valor (ou nas parcelas) deixa o
      // servidor aplicar o preco da nova modalidade
      const trocou = modalidade !== matricula.modalidade;
      const valorMudou = centavos !== matricula.valor_centavos;
      const parcelasMudou = parcelas !== String(matricula.parcelas);
      return escrever('turmas.matricula_alterar', {
        id: matricula.id,
        status,
        modalidade,
        ...(centavos !== null && (!trocou || valorMudou) ? { valor_centavos: centavos } : {}),
        ...(modalidade === 'parcelado' && parcelas !== '' && (!trocou || parcelasMudou)
          ? { parcelas: Number(parcelas) } : {}),
        observacoes: observacoes.trim() === '' ? null : observacoes,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['turmas'] });
      aoFechar();
    },
    onError: setErro,
  });

  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar} titulo={matricula.pessoa.nome}
      subtitulo={`${matricula.turma_nome} · ${matricula.curso_nome}`}
      rodape={podeAlterar ? (
        <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando' : 'Salvar'}
        </Botao>
      ) : undefined}
    >
      <div className="matricula-escolhida" style={{ marginBottom: 'var(--esp-4)' }}>
        <div className="ui-item__principal">
          <span className="ui-item__nome">Ficha do aluno</span>
          <span className="ui-item__detalhe">
            {[matricula.pessoa.telefone_formatado, matricula.pessoa.email].filter(Boolean).join(' · ')}
          </span>
        </div>
        <SeloFicha status={matricula.pessoa.ficha_status} pessoaId={matricula.pessoa.id} />
      </div>
      {erro !== null && <Aviso tom="erro">{mensagensDe(erro)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Status" largura="metade" erro={erroDo('status')}
          {...(status === 'cancelada' && matricula.status !== 'cancelada'
            ? { dica: 'Cancelar libera a vaga.' } : {})}>
          {(id) => (
            <select id={id} value={status} disabled={!podeAlterar}
              onChange={(e) => setStatus(e.target.value as StatusMatricula)}>
              {(Object.keys(STATUS_MATRICULA) as StatusMatricula[]).map((s) => (
                <option key={s} value={s}>{STATUS_MATRICULA[s].rotulo}</option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Modalidade" largura="metade" erro={erroDo('modalidade')}>
          {(id) => (
            <select id={id} value={modalidade} disabled={!podeAlterar}
              onChange={(e) => setModalidade(e.target.value as Modalidade)}>
              <option value="a_vista">À vista</option>
              <option value="parcelado">Parcelado</option>
            </select>
          )}
        </Campo>
        <Campo rotulo="Valor" largura="metade" erro={erroDo('valor_centavos')}
          {...(modalidade !== matricula.modalidade
            ? { dica: 'Sem mudar o valor, vale o preço do curso na nova modalidade.' } : {})}>
          {(id) => <input id={id} inputMode="decimal" value={valor} disabled={!podeAlterar}
            onChange={(e) => setValor(e.target.value)} />}
        </Campo>
        {modalidade === 'parcelado' && (
          <Campo rotulo="Parcelas" largura="metade" erro={erroDo('parcelas')}>
            {(id) => <input id={id} type="number" min={1} max={36} value={parcelas}
              disabled={!podeAlterar} onChange={(e) => setParcelas(e.target.value)} />}
          </Campo>
        )}
        <Campo rotulo="Observações" erro={erroDo('observacoes')}>
          {(id) => <textarea id={id} value={observacoes} disabled={!podeAlterar}
            onChange={(e) => setObservacoes(e.target.value)} />}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

const COLUNAS_MATRICULA: readonly Coluna<Matricula>[] = [
  { id: 'aluno', titulo: 'Aluno', celula: (m) => <strong>{m.pessoa.nome}</strong> },
  { id: 'ficha', titulo: 'Ficha', celula: (m) => <SeloFicha status={m.pessoa.ficha_status} pessoaId={m.pessoa.id} /> },
  { id: 'turma', titulo: 'Turma', celula: (m) => m.turma_nome, secundaria: true },
  {
    id: 'pagamento', titulo: 'Pagamento',
    celula: (m) => `${m.valor_formatado} · ${textoParcelas(m)}`,
  },
  {
    id: 'status', titulo: 'Status',
    celula: (m) => <Selo tom={STATUS_MATRICULA[m.status].tom}>{STATUS_MATRICULA[m.status].rotulo}</Selo>,
  },
];

function AbaMatriculas() {
  const turmas = useTurmas();
  const [turmaId, setTurmaId] = useState('');
  const [status, setStatus] = useState<'' | 'todas' | StatusMatricula>('');
  const [aberta, setAberta] = useState<Matricula | null>(null);
  const [nova, setNova] = useState(false);
  const podeMatricular = usePermissao('turmas.matricular');
  // a matricula pedida por ?id= pode estar fora do filtro (cancelada): procura em todas
  const [idPedido] = useIdPedido();
  const todas = useQuery({
    queryKey: ['turmas', 'matriculas', 'todas'],
    queryFn: () => ler<{ itens: Matricula[] }>('turmas.matriculas_listar',
      { limite: 500, incluir_canceladas: true }),
    enabled: idPedido !== null,
  });
  const limparPedido = useAbrirPedido(todas.data?.itens, setAberta);
  const q = useQuery({
    queryKey: ['turmas', 'matriculas', turmaId, status],
    queryFn: () => ler<{ itens: Matricula[] }>('turmas.matriculas_listar', {
      limite: 500,
      ...(turmaId !== '' ? { turma_id: turmaId } : {}),
      ...(status === 'todas' ? { incluir_canceladas: true }
        : status !== '' ? { status } : {}),
    }),
  });

  return (
    <>
      <Barra acoes={podeMatricular
        ? <Botao variante="primario" aoClicar={() => setNova(true)}>Nova matrícula</Botao>
        : undefined}>
        <select aria-label="Turma" value={turmaId} onChange={(e) => setTurmaId(e.target.value)}>
          <option value="">Todas as turmas</option>
          {(turmas.data?.itens ?? []).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <select aria-label="Status da matrícula" value={status}
          onChange={(e) => setStatus(e.target.value as '' | 'todas' | StatusMatricula)}>
          <option value="">Reservadas e confirmadas</option>
          <option value="reservada">Reservadas</option>
          <option value="confirmada">Confirmadas</option>
          <option value="cancelada">Canceladas</option>
          <option value="todas">Todas</option>
        </select>
      </Barra>

      {q.isPending && <EstadoCarregando texto="Carregando matrículas" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio titulo="Nenhuma matrícula"
          texto={turmaId !== '' || status !== ''
            ? 'Nenhuma matrícula neste filtro.'
            : 'Matricule um aluno numa turma aberta.'} />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS_MATRICULA} linhas={q.data.itens} chave={(m) => m.id}
          aoAbrir={setAberta} />
      )}

      {aberta !== null && (
        <PainelMatricula key={aberta.id} matricula={aberta}
          aoFechar={() => { setAberta(null); limparPedido(); }} />
      )}
      {nova && <PainelNovaMatricula turmaInicial={turmaId} aoFechar={() => setNova(false)} />}
    </>
  );
}

// Aba Cursos e precos

type FormCurso = {
  codigo: string;
  nome: string;
  descricao: string;
  qtd_modulos: string;
  dias_por_modulo: string;
  ativo: boolean;
  a_vista: string;
  parcelado: string;
  parcelas_max: string;
};

function formDoCurso(c: Curso | null): FormCurso {
  const vista = precoDe(c ?? undefined, 'a_vista');
  const parc = precoDe(c ?? undefined, 'parcelado');
  return {
    codigo: c?.codigo ?? '',
    nome: c?.nome ?? '',
    descricao: c?.descricao ?? '',
    qtd_modulos: String(c?.qtd_modulos ?? 1),
    dias_por_modulo: String(c?.dias_por_modulo ?? 3),
    ativo: c?.ativo ?? true,
    a_vista: vista !== undefined ? reais(vista.valor_centavos) : '',
    parcelado: parc !== undefined ? reais(parc.valor_centavos) : '',
    parcelas_max: parc?.parcelas_max != null ? String(parc.parcelas_max) : '',
  };
}

function PainelCurso({ curso, aoFechar }: { curso: Curso | null; aoFechar: () => void }) {
  const qc = useQueryClient();
  const podeEditar = usePermissao('turmas.editar');
  const [form, setForm] = useState<FormCurso>(() => formDoCurso(curso));
  const [erro, setErro] = useState<unknown>(null);

  const salvar = useMutation({
    mutationFn: async () => {
      const salvo = await escrever<Curso>('turmas.curso_salvar', {
        ...(curso !== null ? { id: curso.id } : {}),
        codigo: form.codigo,
        nome: form.nome,
        descricao: form.descricao.trim() === '' ? null : form.descricao,
        qtd_modulos: Number(form.qtd_modulos),
        dias_por_modulo: Number(form.dias_por_modulo),
        ativo: form.ativo,
      });
      // preco vazio fica como esta; preenchido grava
      const vista = centavosDe(form.a_vista);
      if (vista !== null) {
        await escrever('turmas.preco_salvar', {
          curso_id: salvo.id, modalidade: 'a_vista', valor_centavos: vista, parcelas_max: 1,
        });
      }
      const parc = centavosDe(form.parcelado);
      if (parc !== null) {
        await escrever('turmas.preco_salvar', {
          curso_id: salvo.id, modalidade: 'parcelado', valor_centavos: parc,
          parcelas_max: form.parcelas_max === '' ? null : Number(form.parcelas_max),
        });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['turmas'] });
      aoFechar();
    },
    onError: setErro,
  });

  const campo = (k: Exclude<keyof FormCurso, 'ativo'>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));
  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar} titulo={curso === null ? 'Novo curso' : curso.nome}
      subtitulo="Módulos, dias e os preços à vista e parcelado"
      rodape={podeEditar ? (
        <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando' : 'Salvar'}
        </Botao>
      ) : undefined}
    >
      {erro !== null && <Aviso tom="erro">{mensagensDe(erro)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Nome" erro={erroDo('nome')}>
          {(id) => <input id={id} value={form.nome} onChange={campo('nome')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Código" largura="metade" erro={erroDo('codigo')}
          dica="Minúsculas, como formacao. É o que o lead usa em oferta.">
          {(id) => <input id={id} value={form.codigo} onChange={campo('codigo')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Situação" largura="metade">
          {(id) => (
            <select id={id} value={form.ativo ? 'sim' : 'nao'} disabled={!podeEditar}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.value === 'sim' }))}>
              <option value="sim">Ativo</option>
              <option value="nao">Inativo</option>
            </select>
          )}
        </Campo>
        <Campo rotulo="Módulos" largura="metade" erro={erroDo('qtd_modulos')}>
          {(id) => <input id={id} type="number" min={1} max={12} value={form.qtd_modulos}
            onChange={campo('qtd_modulos')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Dias por módulo" largura="metade" erro={erroDo('dias_por_modulo')}>
          {(id) => <input id={id} type="number" min={1} max={30} value={form.dias_por_modulo}
            onChange={campo('dias_por_modulo')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Preço à vista" largura="terco" erro={erroDo('valor_centavos')}>
          {(id) => <input id={id} inputMode="decimal" value={form.a_vista} onChange={campo('a_vista')}
            disabled={!podeEditar} placeholder="R$ 0,00" />}
        </Campo>
        <Campo rotulo="Preço parcelado" largura="terco">
          {(id) => <input id={id} inputMode="decimal" value={form.parcelado} onChange={campo('parcelado')}
            disabled={!podeEditar} placeholder="R$ 0,00" />}
        </Campo>
        <Campo rotulo="Parcelas até" largura="terco" erro={erroDo('parcelas_max')} dica="Vazio: a combinar">
          {(id) => <input id={id} type="number" min={1} max={36} value={form.parcelas_max}
            onChange={campo('parcelas_max')} disabled={!podeEditar} />}
        </Campo>
        <Campo rotulo="Descrição" erro={erroDo('descricao')}>
          {(id) => <textarea id={id} value={form.descricao} onChange={campo('descricao')}
            disabled={!podeEditar} />}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

const COLUNAS_CURSO: readonly Coluna<Curso>[] = [
  { id: 'nome', titulo: 'Curso', celula: (c) => <strong>{c.nome}</strong> },
  {
    id: 'formato', titulo: 'Formato', secundaria: true,
    celula: (c) => `${c.qtd_modulos} módulo(s) de ${c.dias_por_modulo} dia(s)`,
  },
  {
    id: 'vista', titulo: 'À vista',
    celula: (c) => {
      const p = precoDe(c, 'a_vista');
      if (p === undefined) return 'Sem preço';
      // o rotulo embaixo nomeia o preco quando a tabela vira lista no celular
      return (
        <span className="curso-precos">
          <span>{p.valor_formatado}</span>
          <span className="ui-item__detalhe">à vista</span>
        </span>
      );
    },
  },
  {
    id: 'parcelado', titulo: 'Parcelado',
    celula: (c) => {
      const p = precoDe(c, 'parcelado');
      if (p === undefined) return 'Sem preço';
      return (
        <span className="curso-precos">
          <span>{p.valor_formatado}</span>
          <span className="ui-item__detalhe">
            {p.parcelas_max !== null ? `parcelado até ${p.parcelas_max}x` : 'parcelado, a combinar'}
          </span>
        </span>
      );
    },
  },
  {
    id: 'situacao', titulo: 'Situação',
    celula: (c) => <Selo tom={c.ativo ? 'ok' : 'neutro'}>{c.ativo ? 'Ativo' : 'Inativo'}</Selo>,
  },
];

function AbaCursos() {
  const q = useCursos(true);
  const [aberto, setAberto] = useState<Curso | 'novo' | null>(null);
  const podeEditar = usePermissao('turmas.editar');
  const limparPedido = useAbrirPedido(q.data?.itens, setAberto);
  return (
    <>
      <Barra acoes={podeEditar
        ? <Botao variante="primario" aoClicar={() => setAberto('novo')}>Novo curso</Botao>
        : undefined} />
      {q.isPending && <EstadoCarregando texto="Carregando cursos" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio titulo="Nenhum curso no catálogo"
          texto="Cadastre o primeiro curso ou rode a semente do catálogo (npm run semear:tme)." />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS_CURSO} linhas={q.data.itens} chave={(c) => c.id}
          aoAbrir={(c) => setAberto(c)} />
      )}
      {aberto !== null && (
        <PainelCurso key={aberto === 'novo' ? 'novo' : aberto.id}
          curso={aberto === 'novo' ? null : aberto}
          aoFechar={() => { setAberto(null); limparPedido(); }} />
      )}
    </>
  );
}

export const tela: TelaDeModulo = {
  slug: 'turmas',
  rota: '/turmas',
  kicker: 'Cursos e turmas',
  titulo: 'Turmas e matrículas',
  abas: [
    { id: 'turmas', rotulo: 'Turmas', conteudo: AbaTurmas },
    { id: 'matriculas', rotulo: 'Matrículas', conteudo: AbaMatriculas },
    { id: 'cursos', rotulo: 'Cursos e preços', conteudo: AbaCursos },
  ],
  dicionario: { pt: {} },
};
